import { request } from '../../shared/http.js';

// A screen stores its device token here after pairing. It is the screen's only
// credential, and the only one this app ever holds - there is no sign-in here,
// because a living-room display has no keyboard.
const DEVICE_TOKEN_KEY = 'litbyt.deviceToken';

export const getDeviceToken = () => {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY) || '';
  } catch {
    return '';
  }
};

export const setDeviceToken = (token) => {
  try {
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch {
    /* a screen with storage disabled just re-pairs on reload */
  }
};

export const clearDeviceToken = () => {
  try {
    const key = settingsKey();
    if (key) localStorage.removeItem(key);
    localStorage.removeItem(DEVICE_TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

// The settings a display last drew with, so that the next time it starts - a
// reload, a new bundle, the power coming back - it knows which theme it is
// before the API has said so, and its loading screen can be that theme's.
//
// Keyed by the display's token rather than stored under one name, because this
// origin is not one display's. Every display, the admin site's preview frame
// and the marketing site's demo shelf all share it - see authHeaders below for
// what one shared key already cost - and a theme remembered under a single key
// is whichever of those wrote last. A preview remembers nothing: it has no
// token of its own, and it is a warm API away from the answer anyway.
//
// Only ever a first guess. The fetched settings replace it as soon as they
// arrive, and are what it is next written from.
const SETTINGS_KEY = 'litbyt.settings.';

function settingsKey() {
  if (isPreview()) return null;
  const token = getDeviceToken();
  if (!token) return null;
  // A hash rather than the token itself, so the credential is not copied into
  // a second place it would have to be cleaned out of.
  let hash = 5381;
  for (let i = 0; i < token.length; i++) hash = ((hash * 33) ^ token.charCodeAt(i)) >>> 0;
  return SETTINGS_KEY + hash.toString(36);
}

export const recallSettings = () => {
  try {
    const key = settingsKey();
    const stored = key && JSON.parse(localStorage.getItem(key));
    return stored && typeof stored === 'object' ? stored : null;
  } catch {
    return null;
  }
};

export const rememberSettings = (settings) => {
  try {
    const key = settingsKey();
    if (key) localStorage.setItem(key, JSON.stringify(settings));
  } catch {
    /* a display that cannot remember shows a blank screen while it loads */
  }
};

// Preview mode. The admin site embeds this app in an iframe to show what a
// display looks like, and that iframe authenticates with the admin's session
// cookie instead of a device token - which reaches the API because the signage
// and API hostnames share a registrable domain.
//
// It is opt-in through an explicit query parameter and nothing else. A real
// display never carries it, so a display never sends cookies, which is what
// keeps a revoked device from being rescued by whatever session happens to be
// in the browser.
export const isPreview = () => {
  try {
    return new URLSearchParams(window.location.search).has('preview');
  } catch {
    return false;
  }
};

// Which credential a request carries, and this is a choice rather than a
// formality: the server reads a bearer token as the whole answer and never
// falls back to a cookie behind one, because falling back is what let a
// revoked display keep loading off whatever session was in the browser. See
// resolvePrincipal in internal/api/auth.go.
//
// So a preview must send no token at all, not merely prefer the cookie. It
// used to send whatever was in storage, on the assumption that the admin's
// iframe had none - and the iframe has the same origin as every display, so it
// has whatever any display on this host ever stored. The marketing site frames
// the demo shelf at /?code=<token>, and a share link consumes its code into
// storage on sight, so the token of the account on the front page is sitting
// there in the browser of anybody who has ever scrolled down the home page.
// It won, and the preview showed that shelf to whoever was signed in.
function authHeaders() {
  if (isPreview()) return {};
  const token = getDeviceToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// A share link carries the display's token in the URL. Consuming it into
// storage and stripping it from the address bar means a refresh still works,
// and the token stops being visible on screen in a room full of people.
export const takeCodeFromURL = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) return '';

    params.delete('code');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );
    setDeviceToken(code);
    return code;
  } catch {
    return '';
  }
};

// What a display renders with, before the preload has said. The settings
// arrive in the preload rather than on a request of their own: they are what
// the loading screen is drawn from, and the preload is the request that has
// to be quick.
export const DEFAULT_SETTINGS = {
  sortOrder: 'dateAdded',
  sortDirection: 'desc',
  bookshelfLayout: 'stacked',
  bookshelfTheme: 'classic',
};

const read = (path) => request(path, {}, authHeaders(), isPreview() ? 'include' : 'omit');

// What a display reads first: the account's settings, and for each module a
// summary of what it holds - how many items, and how many in each group - and
// a revision that changes whenever they might have. No items. See display.go.
//
//   { settings, pageSize, modules: { bookshelf: { summary, revision } } }
//
// `summary` is null when the API cannot say yet, which is a connection last
// synced before summaries existed; it is never a guess.
export const fetchPreload = () => read('/api/preload');

// The page a display asks for when the preload does not say.
export const PAGE_SIZE = 100;

// One page of a module's items, and the cursor for the next - '' after the
// last. One endpoint serves every module; the module id is the only thing that
// changes, which is what lets a new module ship without new API surface.
export const fetchModulePage = (moduleId, { limit = PAGE_SIZE, cursor = '' } = {}) => {
  const query = new URLSearchParams({ limit: String(limit) });
  if (cursor) query.set('cursor', cursor);
  return read(`/api/modules/${moduleId}/items?${query}`)
    .then((data) => ({ items: data.items ?? [], next: data.next ?? '' }));
};

// Far more pages than any library has, so a cursor that never ends - a
// server bug, not a large shelf - stops rather than walking forever on a
// display nobody is watching.
const MAX_PAGES = 1000;

// Every page of a module, one after another, as they arrive. A page can be
// short or even empty and still not be the last; only an empty cursor ends
// it - see ListItemsPage in the store.
export async function* modulePages(moduleId, limit = PAGE_SIZE) {
  let cursor = '';
  for (let pages = 0; pages < MAX_PAGES; pages++) {
    const page = await fetchModulePage(moduleId, { limit, cursor });
    yield page.items;
    if (!page.next) return;
    cursor = page.next;
  }
  throw new Error(`${moduleId}: still paging after ${MAX_PAGES} pages`);
}

export const startPairing = () => request('/api/devices/pair', { method: 'POST' });
export const pollPairing = (code) => request(`/api/devices/pair/${code}`);
